import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fromIso, toIso } from '../utils/businessDate';
import { getTenantToday } from '../utils/dateUtils';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  RefreshCcw,
  TrendingUp,
  TrendingDown,
  Activity,
  ChevronRight,
  Zap,
  Target,
  Layers,
  PieChart,
  Bot,
  Sparkles,
  Info,
  Calendar,
  Waves,
  ShieldCheck,
  Clock,
  AlertTriangle,
  MoreVertical,
  Briefcase,
  Filter
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { cn } from '../lib/utils';
import LoanExplainDrawer, { LoanRiskRow } from './LoanExplainDrawer';

import { DateField } from './shared/DateField';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> =
  ({ label, value, icon, color, subText }) => (
    <motion.div
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className={cn(
            "text-2xl font-bold tracking-tight",
            value.startsWith('+') ? "text-emerald-400" : value.startsWith('-') ? "text-red-400" : "text-white"
          )}>{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

// ============== Sector Colors ==============
const SECTOR_COLORS: Record<string, string> = {
  TREASURY: '#3b82f6',
  IG_CORP: '#10b981',
  HY_CORP: '#f59e0b',
  MUNI: '#8b5cf6',
  AGENCY: '#06b6d4',
  CASH: '#64748b',
  // New multi-asset sectors
  LEVERAGED_LOANS: '#ec4899',
  DIRECT_LENDING: '#a855f7',
  ABF: '#2dd4bf',
  CLO_EQUITY: '#14b8a6',
  RE_EQUITY: '#f97316',
  RE_DEBT: '#ef4444',
  REIT: '#84cc16',
  PRIVATE_EQUITY: '#6366f1',
  HEDGE_FUND: '#d946ef',
  INFRASTRUCTURE: '#0ea5e9',
};

// ============== Sector Filter Type ==============
type SectorFilter = 'fixed-income' | 'all-sectors' | 'pe' | 're' | 'clo';

// Phase B4 — Brinson alts attribution
interface BrinsonAltsBucket {
  bucket: string;
  startValue: number;
  endValue: number;
  portfolioWeight: number;
  benchmarkWeight: number;
  portfolioReturn: number;
  benchmarkReturn: number;
  allocationEffect: number;
  selectionEffect: number;
  interactionEffect: number;
  contribution: number;
}
interface BrinsonAltsResult {
  assetClass: 'PE' | 'RE' | 'CLO';
  portfolioId: number;
  periodStart: string;
  periodEnd: string;
  benchmarkCode?: string | null;
  portfolioReturn: number;
  benchmarkReturn: number;
  activeReturn: number;
  buckets: BrinsonAltsBucket[];
}

// FI-only sector keys
const FI_SECTORS = new Set([
  'TREASURY',
  'IG_CORP',
  'HY_CORP',
  'MUNI',
  'AGENCY',
  'CASH',
  'ABS',
  'MBS',
  'CMBS',
  'TIPS',
  'SOVEREIGN',
  'LEVERAGED_LOANS',
  'DIRECT_LENDING',
  'ABF',
  'CLO_MEZZ',
  'CLO_EQUITY',
  'RE_DEBT'
]);

// ============== Helper ==============
const irrColor = (irr: number | undefined) => irr == null ? 'text-slate-400' : irr >= 15 ? 'text-emerald-400' : irr >= 8 ? 'text-amber-400' : 'text-red-400';
const fmtM = (v: number) => `$${(v / 1000000).toFixed(1)}M`;
const fmtK = (v: number) => `$${(v / 1000).toFixed(1)}K`;
const fmtPctDec = (v: number | null | undefined, digits = 2) =>
  v == null || isNaN(v as number) ? '—' : `${((v as number) * 100).toFixed(digits)}%`;

// ============== Interfaces ==============
interface Portfolio {
  id: number;
  portfolioName: string;
  portfolioCode?: string;
}

interface Benchmark {
  id: number;
  name: string;
  code: string;
  type?: string;
}

interface AttributionData {
  portfolioReturn: number;
  benchmarkReturn: number;
  excessReturn: number;
  durationEffect: number;
  allocationEffect: number;
  selectionEffect: number;
  interactionEffect: number;
  residual: number;
  overlayContribution?: number;
  portfolioDuration: number;
  benchmarkDuration: number;
  portfolioYield: number;
  benchmarkYield: number;
  sectorDetails: SectorDetail[];
  dataQuality: string;
  periodType: string;
  snapshotStatus?: string;   // CURRENT | LATEST_AVAILABLE | MISSING_SNAPSHOT
  periodStart?: string;
  periodEnd?: string;
  notes?: string;            // benchmark=ACTUAL_INDEX | MODELED
  requestedAsOfDate?: string;
}

interface SectorDetail {
  sector: string;
  portfolioWeight: number;
  benchmarkWeight: number;
  activeWeight: number;
  portfolioReturn: number;
  benchmarkReturn: number;
  allocationEffect: number;
  selectionEffect: number;
  totalEffect: number;
}

interface FundPerformance {
  fundName: string;
  type: string;
  vintage: number;
  // IRR (XIRR over capital-call/distribution timeline) and PME (Kaplan-Schoar)
  // require a benchmark series and full cash-flow timestamps. Until those are
  // wired we surface them as undefined and render "—" in the table rather
  // than fabricate values. tvpi/dpi/moic come straight from the ledger.
  irr?: number;
  tvpi: number;
  dpi: number;
  moic: number;
  pme?: number;
}

interface AlternativesPerformanceData {
  fundPerformance?: FundPerformance[];
}

interface PcLlAttributionLoan {
  loanSource: string;
  loanId: number;
  securityId: number;
  loanName: string;
  borrowerName: string;
  sponsorName?: string;
  sector?: string;
  facilityType?: string;
  beginningMarketValue: number;
  endingMarketValue: number;
  acquisitions: number;
  drawdowns: number;
  disposals: number;
  paydowns: number;
  cashInterest: number;
  oidAccretion: number;
  feeIncome: number;
  pikIncome: number;
  carryPnl: number;
  principalPnl: number;
  realizedPnl: number;
  unrealizedPnlChange: number;
  residual: number;
  totalPnl: number;
  totalReturn: number;
  periodExpectedLoss: number;
  economicCapital: number;
  riskAdjustedPnl: number;
  raroc: number;
  lifecycleDrawdownCount: number;
  lifecyclePaydownCount: number;
  lifecycleAmendmentCount: number;
}

interface PcLlAttributionReport {
  portfolioId: number;
  startDate: string;
  endDate: string;
  methodology: string;
  summary: {
    positionCount: number;
    beginningMarketValue: number;
    endingMarketValue: number;
    acquisitions: number;
    drawdowns: number;
    disposals: number;
    paydowns: number;
    cashInterest: number;
    oidAccretion: number;
    feeIncome: number;
    pikIncome: number;
    carryPnl: number;
    principalPnl: number;
    realizedPnl: number;
    unrealizedPnlChange: number;
    residual: number;
    totalPnl: number;
    totalReturn: number;
    periodExpectedLoss: number;
    economicCapital: number;
    riskAdjustedPnl: number;
    raroc: number;
    trackingError: {
      trackingError: number;
      informationRatio: number;
      observations: number;
      status: string;
      methodology: string;
    };
  };
  loans: PcLlAttributionLoan[];
}

const keyOf = (...parts: Array<string | undefined>) => parts.join(' ').toUpperCase();

const summarizeAttributionError = (value: unknown) => {
  const message = String(value || 'Attribution calculation failed');
  if (message.includes('duplicate key value') && message.includes('fact_attribution_sector_detail')) {
    return 'Attribution recalculation failed while refreshing existing sector rows.';
  }
  return message.length > 180 ? `${message.slice(0, 180)}...` : message;
};

const chooseDefaultBenchmark = (portfolio: Portfolio | undefined, benchmarks: Benchmark[]) => {
  if (!portfolio || benchmarks.length === 0) return benchmarks[0];

  const portfolioKey = keyOf(portfolio.portfolioCode, portfolio.portfolioName);
  const findBenchmark = (matcher: (benchmarkKey: string) => boolean) =>
    benchmarks.find((benchmark) => matcher(keyOf(benchmark.code, benchmark.name, benchmark.type)));

  if (portfolioKey.includes('HIGH YIELD') || portfolioKey.includes('HYB')) {
    return findBenchmark((benchmarkKey) => benchmarkKey.includes('HY00') || benchmarkKey.includes('HIGH YIELD')) || benchmarks[0];
  }

  if (portfolioKey.includes('LEVERAGED') || portfolioKey.includes('LOAN') || portfolioKey.includes('LLF')) {
    return findBenchmark((benchmarkKey) => benchmarkKey.includes('SPLL') || benchmarkKey.includes('LEVERAGED LOAN')) || benchmarks[0];
  }

  // #75: the private-credit fund defaults to HY00 (the data-backed ICE BofA HY
  // index) rather than the custom GCC-CREDIT-BLEND, which has no return series
  // yet (attribution against it is MODELED). Keyed on benchmark CODE, not id,
  // so it resolves correctly across environments. Once the GCC blend is
  // populated with real returns, restore the blend default.
  if (portfolioKey.includes('PRIVATE CREDIT')) {
    return findBenchmark((benchmarkKey) => benchmarkKey.includes('HY00')) || benchmarks[0];
  }

  if (portfolioKey.includes('OPPORTUNISTIC') || portfolioKey.includes('CLO') || portfolioKey.includes('CREDIT')) {
    return findBenchmark((benchmarkKey) => benchmarkKey.includes('CREDIT-BLEND') || benchmarkKey.includes('MULTI-ASSET')) || benchmarks[0];
  }

  return benchmarks[0];
};

const PerformanceAttributionScreen: React.FC = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | null>(null);
  const [selectedBenchmark, setSelectedBenchmark] = useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('MTD');
  const [customPeriodStart, setCustomPeriodStart] = useState<string>('');
  const [customPeriodEnd, setCustomPeriodEnd] = useState<string>('');
  const [attributionData, setAttributionData] = useState<{ [key: string]: AttributionData }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiExplanation, setAiExplanation] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [sectorFilter, setSectorFilter] = useState<SectorFilter>('fixed-income');
  const [alternativesData, setAlternativesData] = useState<AlternativesPerformanceData | null>(null);
  const [brinsonAlts, setBrinsonAlts] = useState<BrinsonAltsResult | null>(null);
  const [brinsonLoading, setBrinsonLoading] = useState(false);
  const [brinsonError, setBrinsonError] = useState<string | null>(null);
  const [pcLlAttribution, setPcLlAttribution] = useState<PcLlAttributionReport | null>(null);
  const [pcLlLoading, setPcLlLoading] = useState(false);
  const [pcLlError, setPcLlError] = useState<string | null>(null);
  const [pcLlRecalculating, setPcLlRecalculating] = useState(false);
  const [pcLlReloadKey, setPcLlReloadKey] = useState(0);
  const [summaryRefreshing, setSummaryRefreshing] = useState(false);
  const [pcLlRisk, setPcLlRisk] = useState<LoanRiskRow[]>([]);
  const [selectedLoanKey, setSelectedLoanKey] = useState<string | null>(null);

  // Phase B4 — derive periodStart/periodEnd from MTD/QTD/YTD selection.
  // PC/LL event validation also needs ad-hoc windows (e.g. day before a paydown
  // through today), so CUSTOM uses the operator-entered dates when supplied.
  const periodRange = useMemo(() => {
    const today = fromIso(getTenantToday());
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();
    const end = new Date(y, m, d);
    let start: Date;
    const fmt = (dt: Date) => toIso(dt);
    if (selectedPeriod === 'CUSTOM') {
      const fallbackEnd = fmt(end);
      return {
        periodStart: customPeriodStart || fallbackEnd,
        periodEnd: customPeriodEnd || fallbackEnd,
      };
    }
    if (selectedPeriod === 'MTD') {
      start = new Date(y, m, 1);
    } else if (selectedPeriod === 'QTD') {
      const qStartMonth = Math.floor(m / 3) * 3;
      start = new Date(y, qStartMonth, 1);
    } else {
      start = new Date(y, 0, 1);
    }
    return { periodStart: fmt(start), periodEnd: fmt(end) };
  }, [selectedPeriod, customPeriodStart, customPeriodEnd]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [portfolioRes, benchmarkRes] = await Promise.all([
          axios.get('/api/dimensions/portfolios'),
          axios.get('/api/attribution/benchmarks')
        ]);
        const pList = portfolioRes.data?.content || portfolioRes.data || [];
        const bList = benchmarkRes.data?.content || benchmarkRes.data || [];
        const normalizedPortfolios = Array.isArray(pList) ? pList : [];
        const normalizedBenchmarks = Array.isArray(bList) ? bList : [];
        setPortfolios(normalizedPortfolios);
        setBenchmarks(normalizedBenchmarks);

        if (normalizedPortfolios.length > 0) {
          const firstPortfolio = normalizedPortfolios[0];
          setSelectedPortfolio(firstPortfolio.id);
          const defaultBenchmark = chooseDefaultBenchmark(firstPortfolio, normalizedBenchmarks);
          if (defaultBenchmark) setSelectedBenchmark(defaultBenchmark.id);
        } else if (normalizedBenchmarks.length > 0) {
          setSelectedBenchmark(normalizedBenchmarks[0].id);
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
      }
    };
    fetchData();
  }, []);

  const fetchAttribution = useCallback(async () => {
    if (!selectedPortfolio || !selectedBenchmark) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/attribution/portfolio/${selectedPortfolio}/summary`, {
        params: { benchmarkId: selectedBenchmark }
      });
      const data: { [key: string]: AttributionData } = {};
      const periodErrors: string[] = [];
      if (response.data.mtd && !response.data.mtd.error) data.MTD = response.data.mtd;
      else if (response.data.mtd?.error) periodErrors.push(`MTD: ${summarizeAttributionError(response.data.mtd.error)}`);
      if (response.data.qtd && !response.data.qtd.error) data.QTD = response.data.qtd;
      else if (response.data.qtd?.error) periodErrors.push(`QTD: ${summarizeAttributionError(response.data.qtd.error)}`);
      if (response.data.ytd && !response.data.ytd.error) data.YTD = response.data.ytd;
      else if (response.data.ytd?.error) periodErrors.push(`YTD: ${summarizeAttributionError(response.data.ytd.error)}`);
      setAttributionData(data);
      setError(periodErrors.length > 0 ? periodErrors.join(' ') : null);

      // Also fetch alternatives performance if available
      if (response.data.alternativesMetrics) {
        setAlternativesData(response.data.alternativesMetrics);
      } else {
        // Try fetching from risk endpoint as fallback
        try {
          const riskRes = await axios.get(`/api/risk/portfolio/${selectedPortfolio}`);
          if (riskRes.data?.alternativesMetrics?.fundPerformance) {
            setAlternativesData(riskRes.data.alternativesMetrics);
          } else {
            setAlternativesData(null);
          }
        } catch {
          setAlternativesData(null);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to calculate attribution');
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolio, selectedBenchmark]);

  useEffect(() => {
    fetchAttribution();
  }, [fetchAttribution]);

  // #79: the top "Refresh" was a bare GET (read-only) — it re-read the same
  // stale persisted Brinson rows and never ran the producer. Recalculate =
  // POST the sanctioned /summary/refresh producer, then re-read. Reads still
  // never calculate; this is how the top panel advances a stale period.
  const handleRefreshSummary = useCallback(async () => {
    if (!selectedPortfolio || !selectedBenchmark || summaryRefreshing) return;
    setSummaryRefreshing(true);
    try {
      await axios.post(`/api/attribution/portfolio/${selectedPortfolio}/summary/refresh`, null, {
        params: { benchmarkId: selectedBenchmark }
      });
      await fetchAttribution();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to recalculate attribution');
    } finally {
      setSummaryRefreshing(false);
    }
  }, [selectedPortfolio, selectedBenchmark, summaryRefreshing, fetchAttribution]);

  useEffect(() => {
    if (!selectedPortfolio) {
      setPcLlAttribution(null);
      setPcLlError(null);
      return;
    }
    let cancelled = false;
    setPcLlLoading(true);
    setPcLlError(null);
    const params = {
      startDate: periodRange.periodStart,
      endDate: periodRange.periodEnd,
    };
    const load = async () => {
      try {
        const res = await axios.get(
          `/api/credit/pc-ll-analytics/attribution/portfolio/${selectedPortfolio}`, { params });
        if (res.data?.snapshotStatus === 'MISSING_SNAPSHOT') {
          if (!cancelled) {
            setPcLlError(res.data.error || 'PC/LL attribution snapshot is not available for this period');
            setPcLlAttribution(null);
          }
          return;
        }
        if (!cancelled) setPcLlAttribution(res.data || null);
      } catch (err: any) {
        if (cancelled) return;
        setPcLlError(err.response?.data?.error || err.message || 'Failed to load PC/LL attribution');
        setPcLlAttribution(null);
      } finally {
        if (!cancelled) setPcLlLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedPortfolio, periodRange, pcLlReloadKey]);

  // #68: sanctioned on-demand producer for the PC/LL attribution snapshot —
  // the GET above never calculates, so a MISSING_SNAPSHOT period needs this
  // POST (same pattern as the risk sleeve's Recalculate).
  const handleRecalculatePcLl = useCallback(async () => {
    if (!selectedPortfolio || pcLlRecalculating) return;
    setPcLlRecalculating(true);
    try {
      await axios.post(
        `/api/credit/pc-ll-analytics/attribution/portfolio/${selectedPortfolio}/refresh`, null, {
          params: {
            startDate: periodRange.periodStart,
            endDate: periodRange.periodEnd,
          },
        });
      setPcLlReloadKey(k => k + 1); // re-run the read effect against the fresh snapshot
    } catch (err: any) {
      setPcLlError(err.response?.data?.error || err.message || 'Failed to recalculate PC/LL attribution');
    } finally {
      setPcLlRecalculating(false);
    }
  }, [selectedPortfolio, periodRange, pcLlRecalculating]);

  // Fetch PC/LL per-loan risk rows (PD/LGD/EAD, DV01, covenants) to power the
  // "explain every number" drawer. Same global-interceptor auth as the
  // attribution fetch above. Keyed `${loanSource}-${loanId}` on lookup.
  useEffect(() => {
    if (!selectedPortfolio) {
      setPcLlRisk([]);
      return;
    }
    let cancelled = false;
    const loadRisk = async () => {
      try {
        const res = await axios.get(`/api/credit/pc-ll-analytics/risk/portfolio/${selectedPortfolio}`);
        if (cancelled) return;
        const data = res.data;
        const rows: LoanRiskRow[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.loans)
          ? data.loans
          : Array.isArray(data?.content)
          ? data.content
          : [];
        setPcLlRisk(rows);
      } catch {
        if (!cancelled) setPcLlRisk([]);
      }
    };
    loadRisk();
    return () => {
      cancelled = true;
    };
  }, [selectedPortfolio, pcLlReloadKey]);

  // Lookup map for risk rows, keyed `${loanSource}-${loanId}`.
  const pcLlRiskByKey = useMemo(() => {
    const map = new Map<string, LoanRiskRow>();
    pcLlRisk.forEach((r) => map.set(`${r.loanSource}-${r.loanId}`, r));
    return map;
  }, [pcLlRisk]);

  // Keep the drawer bound to the current governed snapshot. Holding the whole
  // row object here left the explain view stale after a lifecycle refresh even
  // though the table had already reloaded.
  const selectedLoan = useMemo(() => {
    if (!selectedLoanKey) return null;
    return pcLlAttribution?.loans.find(
      loan => `${loan.loanSource}-${loan.loanId}` === selectedLoanKey
    ) || null;
  }, [pcLlAttribution, selectedLoanKey]);

  useEffect(() => {
    setSelectedLoanKey(null);
  }, [selectedPortfolio]);

  // Phase B4 — fetch Brinson alts attribution when sectorFilter is PE/RE/CLO
  useEffect(() => {
    if (!selectedPortfolio) {
      setBrinsonAlts(null);
      return;
    }
    if (sectorFilter !== 'pe' && sectorFilter !== 're' && sectorFilter !== 'clo') {
      setBrinsonAlts(null);
      setBrinsonError(null);
      return;
    }
    const assetClass = sectorFilter.toUpperCase();
    let cancelled = false;
    setBrinsonLoading(true);
    setBrinsonError(null);
    axios
      .get(`/api/analytics/brinson-alts/${assetClass}/portfolios/${selectedPortfolio}`, {
        params: {
          periodStart: periodRange.periodStart,
          periodEnd: periodRange.periodEnd,
        },
      })
      .then((res) => {
        if (cancelled) return;
        setBrinsonAlts(res.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setBrinsonError(err.response?.data?.error || err.message || 'Failed to load Brinson attribution');
        setBrinsonAlts(null);
      })
      .finally(() => {
        if (!cancelled) setBrinsonLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sectorFilter, selectedPortfolio, periodRange]);

  const generateAiExplanation = async () => {
    const data = attributionData[selectedPeriod];
    if (!data) return;
    setAiLoading(true);
    try {
      const response = await axios.post('/api/ai/attribution/explain', {
        ...data,
        period: selectedPeriod
      });
      setAiExplanation(response.data);
    } catch (error) {
      console.error('Error generating AI explanation:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const currentData = attributionData[selectedPeriod];
  const portfolioHistoryMissing = Boolean(currentData?.notes?.includes('positions=NO_SNAPSHOT_WINDOW'));
  const benchmarkModeled = Boolean(currentData?.notes?.toUpperCase().includes('MODELED'));
  const pcLlTrackingStatus = pcLlAttribution?.summary?.trackingError?.status;
  const pcLlTrackingIncomplete = Boolean(pcLlTrackingStatus && pcLlTrackingStatus !== 'CALCULATED');
  const displayedDataQuality = currentData
    ? (benchmarkModeled || pcLlTrackingIncomplete ? 'ESTIMATED' : currentData.dataQuality)
    : 'N/A';
  const dataQualitySubText = benchmarkModeled
    ? 'Benchmark modeled'
    : portfolioHistoryMissing
    ? 'Portfolio snapshot history unavailable'
    : pcLlTrackingIncomplete
    ? `${pcLlTrackingStatus} tracking history`
    : 'Benchmark = actual index';

  // Filter sector details based on sector filter
  const filteredSectorDetails = useMemo(() => {
    if (!currentData?.sectorDetails) return [];
    if (sectorFilter === 'all-sectors') return currentData.sectorDetails;
    return currentData.sectorDetails.filter(s => FI_SECTORS.has(s.sector));
  }, [currentData, sectorFilter]);

  const waterfallData = currentData ? {
    labels: ['Duration', 'Allocation', 'Selection', 'Interaction', 'Residual', 'Alpha'],
    datasets: [{
      data: [
        currentData.durationEffect,
        currentData.allocationEffect,
        currentData.selectionEffect,
        currentData.interactionEffect,
        currentData.residual,
        currentData.excessReturn
      ],
      backgroundColor: [
        currentData.durationEffect >= 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
        currentData.allocationEffect >= 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
        currentData.selectionEffect >= 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
        currentData.interactionEffect >= 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
        currentData.residual >= 0 ? 'rgba(148, 163, 184, 0.2)' : 'rgba(239, 68, 68, 0.2)',
        'rgba(59, 130, 246, 0.4)'
      ],
      borderColor: [
        currentData.durationEffect >= 0 ? '#10b981' : '#ef4444',
        currentData.allocationEffect >= 0 ? '#10b981' : '#ef4444',
        currentData.selectionEffect >= 0 ? '#10b981' : '#ef4444',
        currentData.interactionEffect >= 0 ? '#10b981' : '#ef4444',
        currentData.residual >= 0 ? '#94a3b8' : '#ef4444',
        '#3b82f6'
      ],
      borderWidth: 1,
      borderRadius: 8
    }]
  } : null;

  const hasAlternativesPerformance = alternativesData?.fundPerformance && alternativesData.fundPerformance.length > 0;

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-blue-500" />
                Return Attribution
              </h1>
              <p className="text-slate-500 font-medium mt-1">Brinson-Fachler decomposition of excess returns relative to institutional benchmarks</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1.5 rounded-xl">
                <select
                  value={selectedPortfolio || ''}
                  onChange={(e) => {
                    const portfolioId = Number(e.target.value);
                    setSelectedPortfolio(portfolioId);
                    const portfolio = portfolios.find((p) => p.id === portfolioId);
                    const defaultBenchmark = chooseDefaultBenchmark(portfolio, benchmarks);
                    if (defaultBenchmark) setSelectedBenchmark(defaultBenchmark.id);
                  }}
                  className="bg-transparent text-xs font-bold text-slate-300 px-3 focus:outline-none cursor-pointer"
                >
                  {portfolios.map(p => <option key={p.id} value={p.id} className="bg-slate-900">{p.portfolioName}</option>)}
                </select>
                <div className="w-px h-4 bg-white/10 mx-2 self-center" />
                <select
                  value={selectedBenchmark || ''}
                  onChange={(e) => setSelectedBenchmark(Number(e.target.value))}
                  className="bg-transparent text-xs font-bold text-slate-300 px-3 focus:outline-none cursor-pointer"
                >
                  {benchmarks.map(b => <option key={b.id} value={b.id} className="bg-slate-900">{b.name}</option>)}
                </select>
              </div>
              <button
                onClick={handleRefreshSummary}
                disabled={summaryRefreshing}
                title="Recalculate and publish the attribution snapshot, then re-read"
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all disabled:opacity-50"
              >
                <RefreshCcw className={cn("w-4 h-4", (loading || summaryRefreshing) && "animate-spin")} />
                {summaryRefreshing ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-white/5 pb-1">
            <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
              {['MTD', 'QTD', 'YTD', 'CUSTOM'].map(period => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={cn(
                    "px-6 py-1.5 rounded-lg text-xs font-bold transition-all",
                    selectedPeriod === period ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  {period}
                </button>
              ))}
            </div>
            {selectedPeriod === 'CUSTOM' && (
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900 px-3 py-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <DateField compact
                  value={customPeriodStart ?? ''}
                  aria-label="PC/LL attribution custom start date"
                  onChange={iso => setCustomPeriodStart(iso)}
                />
                <span className="text-xs font-bold text-slate-500">→</span>
                <DateField compact
                  value={customPeriodEnd ?? ''}
                  aria-label="PC/LL attribution custom end date"
                  onChange={iso => setCustomPeriodEnd(iso)}
                />
              </div>
            )}
            <button
              onClick={generateAiExplanation}
              disabled={aiLoading || !currentData}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-indigo-600/20 uppercase tracking-widest"
            >
              {aiLoading ? <Bot className="w-4 h-4 animate-bounce" /> : <Sparkles className="w-4 h-4" />}
              AI Decomposition
            </button>
          </div>

          {error && (
            <div role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-300">
              {error}
            </div>
          )}

          {/* #79: disclose when the top Brinson panel is serving a stale
              (latest-available) snapshot, and offer the producer — the panel
              used to show old numbers silently. */}
          {currentData?.snapshotStatus === 'LATEST_AVAILABLE' && (
            <div className="flex items-center gap-3 rounded-xl border border-cyan-400/25 bg-cyan-500/10 px-4 py-3 text-sm font-semibold text-cyan-100">
              <Clock className="h-4 w-4 text-cyan-300" />
              <span className="flex-1">
                Showing latest persisted {selectedPeriod} attribution
                {currentData.periodEnd ? ` through ${currentData.periodEnd}` : ''}
                {currentData.requestedAsOfDate ? ` (requested ${currentData.requestedAsOfDate})` : ''}.
              </span>
              <button
                onClick={handleRefreshSummary}
                disabled={summaryRefreshing}
                className="rounded-lg border border-cyan-300/40 bg-cyan-400/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-cyan-100 transition-colors hover:bg-cyan-400/30 disabled:opacity-50"
              >
                {summaryRefreshing ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          )}
          {/* #79: benchmark honesty — a MODELED benchmark means the alpha is
              not measured against a real index. */}
          {benchmarkModeled && (
            <div className="flex items-center gap-2 rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-2 text-xs font-semibold text-amber-100">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />
              <span>Benchmark is <b>modeled</b> (no index return series for this benchmark) — excess return is indicative, not measured.</span>
            </div>
          )}
          {pcLlTrackingIncomplete && (
            <div className="flex items-center gap-2 rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-2 text-xs font-semibold text-amber-100">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />
              <span>PC/LL tracking history is <b>{pcLlTrackingStatus}</b> — tracking error and information ratio are not complete.</span>
            </div>
          )}
          {portfolioHistoryMissing && (
            <div className="flex items-center gap-2 rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-2 text-xs font-semibold text-amber-100" role="status">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />
              <span>Portfolio return and attribution are unavailable for this period because the required position snapshots are missing.</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Portfolio Return" value={portfolioHistoryMissing ? '—' : `${(currentData?.portfolioReturn || 0).toFixed(4)}%`} icon={<Activity className="w-5 h-5" />} color="#3b82f6" />
            <StatCard label="Benchmark Return" value={`${(currentData?.benchmarkReturn || 0).toFixed(4)}%`} icon={<Target className="w-5 h-5" />} color="#8b5cf6" />
            <StatCard label="Excess Return (Alpha)" value={portfolioHistoryMissing ? '—' : `${(currentData?.excessReturn || 0).toFixed(4)}%`} icon={<Zap className="w-5 h-5" />} color={currentData?.excessReturn >= 0 ? "#10b981" : "#ef4444"} subText="Return over benchmark" />
            <StatCard label="Data Quality Score" value={portfolioHistoryMissing ? 'INSUFFICIENT' : displayedDataQuality} icon={<ShieldCheck className="w-5 h-5" />} color="#f59e0b" subText={dataQualitySubText} />
          </div>
        </motion.div>

        <GlassCard className="overflow-hidden">
          <div className="p-6 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Briefcase className="w-5 h-5 text-purple-400" />
              <div>
                <h3 className="text-lg font-bold text-white">PC / LL Lifecycle Attribution</h3>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                  Posted loan trades, drawdowns, paydowns, accruals, PIK, OID, fees, and mark movement
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                {periodRange.periodStart} → {periodRange.periodEnd}
              </div>
              <button
                onClick={handleRecalculatePcLl}
                disabled={pcLlRecalculating || !selectedPortfolio}
                title="Recalculate and publish the PC/LL lifecycle attribution snapshot"
                className="rounded-lg border border-amber-300/40 bg-amber-400/20 px-4 py-2 text-[10px] font-bold uppercase tracking-wide text-amber-100 transition-colors hover:bg-amber-400/30 disabled:opacity-50"
              >
                {pcLlRecalculating ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          </div>

          {pcLlLoading && (
            <div className="p-8 text-sm text-slate-500 italic">Loading PC/LL attribution…</div>
          )}
          {pcLlError && !pcLlLoading && (
            <div className="p-8 flex items-center gap-4">
              <span className="flex-1 text-sm text-red-400 italic">{pcLlError}</span>
              {/* #68: missing/stale snapshot is a producer problem — offer the producer. */}
              <button
                onClick={handleRecalculatePcLl}
                disabled={pcLlRecalculating}
                className="rounded-lg border border-amber-300/40 bg-amber-400/20 px-4 py-2 text-xs font-bold uppercase tracking-wide text-amber-100 transition-colors hover:bg-amber-400/30 disabled:opacity-50"
              >
                {pcLlRecalculating ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          )}
          {!pcLlLoading && !pcLlError && pcLlAttribution?.summary && pcLlAttribution.summary.positionCount > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-6 border-b border-white/5">
                <StatCard label="PC/LL Total Return" value={fmtPctDec(pcLlAttribution.summary.totalReturn, 2)} icon={<TrendingUp className="w-5 h-5" />} color={pcLlAttribution.summary.totalReturn >= 0 ? "#10b981" : "#ef4444"} subText={`${pcLlAttribution.summary.positionCount} positions`} />
                <StatCard label="Total P&L" value={fmtK(pcLlAttribution.summary.totalPnl)} icon={<Activity className="w-5 h-5" />} color="#3b82f6" subText={`Carry ${fmtK(pcLlAttribution.summary.carryPnl)} / Principal ${fmtK(pcLlAttribution.summary.principalPnl)}`} />
                <StatCard label="Cash + PIK Income" value={fmtK(pcLlAttribution.summary.cashInterest + pcLlAttribution.summary.pikIncome)} icon={<Zap className="w-5 h-5" />} color="#a855f7" subText={`OID/fees ${fmtK(pcLlAttribution.summary.oidAccretion + pcLlAttribution.summary.feeIncome)}`} />
                <StatCard label="RAROC" value={fmtPctDec(pcLlAttribution.summary.raroc, 2)} icon={<ShieldCheck className="w-5 h-5" />} color="#14b8a6" subText={`Risk-adj ${fmtK(pcLlAttribution.summary.riskAdjustedPnl)} / capital ${fmtK(pcLlAttribution.summary.economicCapital)}`} />
                <StatCard label="Tracking Error" value={fmtPctDec(pcLlAttribution.summary.trackingError?.trackingError, 2)} icon={<Target className="w-5 h-5" />} color="#f59e0b" subText={`${pcLlAttribution.summary.trackingError?.status || 'N/A'} / ${pcLlAttribution.summary.trackingError?.observations || 0} obs`} />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
                      <th className="py-4 px-6">Borrower</th>
                      <th className="py-4 px-6">Type</th>
                      <th className="py-4 px-6 text-right">Begin MV</th>
                      <th className="py-4 px-6 text-right">End MV</th>
                      <th className="py-4 px-6 text-right">Drawdowns</th>
                      <th className="py-4 px-6 text-right">Paydowns</th>
                      <th className="py-4 px-6 text-right">Carry</th>
                      <th className="py-4 px-6 text-right">Principal P&L</th>
                      <th className="py-4 px-6 text-right">Total P&L</th>
                      <th className="py-4 px-6 text-right">Return</th>
                      <th className="py-4 px-6 text-right">RAROC</th>
                      <th className="py-4 px-6 text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {pcLlAttribution.loans.slice(0, 25).map((loan) => (
                      <tr
                        key={`${loan.loanSource}-${loan.loanId}`}
                        onClick={() => setSelectedLoanKey(`${loan.loanSource}-${loan.loanId}`)}
                        className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 cursor-pointer"
                      >
                        <td className="py-4 px-6">
                          <div className="font-bold text-white">{loan.borrowerName || loan.loanName}</div>
                          <div className="text-[11px] text-slate-500">{loan.sponsorName || loan.sector || loan.facilityType || '—'}</div>
                        </td>
                        <td className="py-4 px-6 font-mono text-slate-300">{loan.loanSource === 'PRIVATE_CREDIT' ? 'PC' : 'LL'}</td>
                        <td className="py-4 px-6 text-right font-mono text-slate-300">{fmtM(loan.beginningMarketValue)}</td>
                        <td className="py-4 px-6 text-right font-mono text-slate-300">{fmtM(loan.endingMarketValue)}</td>
                        <td className="py-4 px-6 text-right font-mono text-slate-300">{fmtM(loan.drawdowns + loan.acquisitions)}</td>
                        <td className="py-4 px-6 text-right font-mono text-slate-300">{fmtM(loan.paydowns + loan.disposals)}</td>
                        <td className="py-4 px-6 text-right font-mono text-slate-300">{fmtK(loan.carryPnl)}</td>
                        <td className={cn("py-4 px-6 text-right font-mono", loan.principalPnl >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {fmtK(loan.principalPnl)}
                        </td>
                        <td className={cn("py-4 px-6 text-right font-mono font-bold", loan.totalPnl >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {fmtK(loan.totalPnl)}
                        </td>
                        <td className={cn("py-4 px-6 text-right font-mono font-bold", loan.totalReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {fmtPctDec(loan.totalReturn, 2)}
                        </td>
                        <td className={cn("py-4 px-6 text-right font-mono font-bold", loan.raroc >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {fmtPctDec(loan.raroc, 2)}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 group-hover:text-blue-400 transition-colors">
                            Explain
                            <ChevronRight className="w-3.5 h-3.5" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!pcLlLoading && !pcLlError && (!pcLlAttribution || pcLlAttribution.summary.positionCount === 0) && (
            <div className="p-8 text-sm text-slate-500 italic">No posted private credit or leveraged-loan positions found for this period.</div>
          )}
        </GlassCard>

        <AnimatePresence>
          {aiExplanation && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 text-indigo-400">
                  <Bot className="w-5 h-5" />
                  <span className="text-xs font-bold uppercase tracking-widest text-white">AI Commentary - {selectedPeriod}</span>
                </div>
                <button onClick={() => setAiExplanation(null)} className="text-slate-500 hover:text-white transition-colors">×</button>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed italic">{aiExplanation.explanation}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {!portfolioHistoryMissing && <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          <GlassCard className="xl:col-span-2 p-6 flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Waves className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-bold text-white">Attribution Waterfall</h3>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase">Positive Effect</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase">Negative Effect</span>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-[300px]">
              {waterfallData && (
                <Bar
                  data={waterfallData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#64748b', font: { weight: 'bold', size: 10 } } },
                      x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { weight: 'bold', size: 10 } } }
                    }
                  }}
                />
              )}
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <Layers className="w-5 h-5 text-indigo-400" />
              <h3 className="text-lg font-bold text-white">Effect Breakdown</h3>
            </div>
            <div className="space-y-6">
              {[
                { label: 'Duration Effect', value: currentData?.durationEffect, desc: 'Impact from yield curve movements' },
                { label: 'Allocation Effect', value: currentData?.allocationEffect, desc: 'Impact from sector weighting decisions' },
                { label: 'Selection Effect', value: currentData?.selectionEffect, desc: 'Impact from specific security choices' },
                { label: 'Interaction Effect', value: currentData?.interactionEffect, desc: 'Cross-effect between active weights and sector returns' },
                { label: 'Derivatives Overlay', value: currentData?.overlayContribution, desc: 'Carve-out contribution: derivative MV change + derivative cash (V466) — sector effects cover the invested sleeve only' },
                { label: 'Residual Return', value: currentData?.residual, desc: 'Unexplained return components (net of the overlay)' }
              ].map((effect, i) => (
                <div key={i} className="p-4 bg-slate-950/30 border border-white/5 rounded-2xl">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-bold text-slate-300">{effect.label}</span>
                    <span className={cn(
                      "font-mono font-bold text-sm",
                      (effect.value || 0) >= 0 ? "text-emerald-400" : "text-red-400"
                    )}>
                      {(effect.value || 0) >= 0 ? '+' : ''}{(effect.value || 0).toFixed(4)}%
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 font-medium">{effect.desc}</div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>}

        <GlassCard className="overflow-hidden">
          <div className="p-6 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PieChart className="w-5 h-5 text-blue-400" />
              <h3 className="text-lg font-bold text-white">Sector Decomposition Detail</h3>
            </div>
            <div className="flex items-center gap-3">
              {/* Sector Filter Toggle */}
              <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
                <button
                  onClick={() => setSectorFilter('fixed-income')}
                  className={cn(
                    "px-4 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all flex items-center gap-1.5",
                    sectorFilter === 'fixed-income'
                      ? "bg-blue-600 text-white shadow-lg"
                      : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  <Filter className="w-3 h-3" />
                  Fixed Income
                </button>
                <button
                  onClick={() => setSectorFilter('all-sectors')}
                  className={cn(
                    "px-4 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all flex items-center gap-1.5",
                    sectorFilter === 'all-sectors'
                      ? "bg-blue-600 text-white shadow-lg"
                      : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  <Layers className="w-3 h-3" />
                  All Sectors
                </button>
                <button
                  onClick={() => setSectorFilter('pe')}
                  className={cn(
                    "px-4 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all",
                    sectorFilter === 'pe'
                      ? "bg-indigo-600 text-white shadow-lg"
                      : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  PE
                </button>
                <button
                  onClick={() => setSectorFilter('re')}
                  className={cn(
                    "px-4 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all",
                    sectorFilter === 're'
                      ? "bg-orange-600 text-white shadow-lg"
                      : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  RE
                </button>
                <button
                  onClick={() => setSectorFilter('clo')}
                  className={cn(
                    "px-4 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all",
                    sectorFilter === 'clo'
                      ? "bg-teal-600 text-white shadow-lg"
                      : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  CLO
                </button>
              </div>
              <button className="p-2 text-slate-500 hover:text-white transition-colors">
                <MoreVertical className="w-5 h-5" />
              </button>
            </div>
          </div>
          {(sectorFilter === 'pe' || sectorFilter === 're' || sectorFilter === 'clo') && (
            <div className="px-6 py-4 border-b border-white/5 bg-slate-950/20 flex items-center gap-6 text-xs">
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Asset Class</div>
                <div className="font-bold text-white mt-0.5">{sectorFilter.toUpperCase()}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Period</div>
                <div className="font-mono text-slate-300 mt-0.5">{periodRange.periodStart} → {periodRange.periodEnd}</div>
              </div>
              {brinsonAlts && (
                <>
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Portfolio Return</div>
                    <div className="font-mono font-bold text-white mt-0.5">{(brinsonAlts.portfolioReturn * 100).toFixed(4)}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Benchmark Return</div>
                    <div className="font-mono font-bold text-slate-300 mt-0.5">
                      {(brinsonAlts.benchmarkReturn * 100).toFixed(4)}%
                      {brinsonAlts.benchmarkCode && <span className="text-[10px] text-slate-500 ml-2">({brinsonAlts.benchmarkCode})</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Active Return</div>
                    <div className={cn("font-mono font-bold mt-0.5", brinsonAlts.activeReturn >= 0 ? "text-emerald-400" : "text-red-400")}>
                      {brinsonAlts.activeReturn >= 0 ? '+' : ''}{(brinsonAlts.activeReturn * 100).toFixed(4)}%
                    </div>
                  </div>
                </>
              )}
              {brinsonLoading && <span className="text-slate-500 italic">Loading…</span>}
              {brinsonError && <span className="text-red-400 italic">{brinsonError}</span>}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
                  <th className="py-4 px-6">{(sectorFilter === 'pe' || sectorFilter === 're' || sectorFilter === 'clo') ? 'Bucket' : 'Sector Name'}</th>
                  <th className="py-4 px-6">Active Weight</th>
                  <th className="py-4 px-6">Allocation Effect</th>
                  <th className="py-4 px-6">Selection Effect</th>
                  <th className="py-4 px-6">Interaction</th>
                  <th className="py-4 px-6 text-right">Total Contribution</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {(sectorFilter === 'pe' || sectorFilter === 're' || sectorFilter === 'clo') ? (
                  <>
                    {brinsonAlts?.buckets?.map((b, i) => {
                      const activeWeight = (b.portfolioWeight - b.benchmarkWeight) * 100;
                      const allocationEffectPct = b.allocationEffect * 100;
                      const selectionEffectPct = b.selectionEffect * 100;
                      const interactionEffectPct = b.interactionEffect * 100;
                      const contributionPct = b.contribution * 100;
                      const bucketColor =
                        sectorFilter === 'pe' ? '#6366f1' :
                        sectorFilter === 're' ? '#f97316' : '#14b8a6';
                      return (
                        <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                          <td className="py-4 px-6 font-bold text-white">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: bucketColor }} />
                              {b.bucket}
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={cn("font-mono font-bold", activeWeight >= 0 ? "text-blue-400" : "text-amber-400")}>
                              {activeWeight >= 0 ? '+' : ''}{activeWeight.toFixed(2)}%
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={cn("font-mono", allocationEffectPct >= 0 ? "text-emerald-400" : "text-red-400")}>
                              {allocationEffectPct >= 0 ? '+' : ''}{allocationEffectPct.toFixed(4)}%
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={cn("font-mono", selectionEffectPct >= 0 ? "text-emerald-400" : "text-red-400")}>
                              {selectionEffectPct >= 0 ? '+' : ''}{selectionEffectPct.toFixed(4)}%
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={cn("font-mono text-slate-400")}>
                              {interactionEffectPct >= 0 ? '+' : ''}{interactionEffectPct.toFixed(4)}%
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right font-bold text-white">
                            {contributionPct >= 0 ? '+' : ''}{contributionPct.toFixed(4)}%
                          </td>
                        </tr>
                      );
                    })}
                    {(!brinsonAlts || brinsonAlts.buckets.length === 0) && !brinsonLoading && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-500 italic text-sm">
                          {brinsonError ? 'Unable to compute Brinson attribution for this period.' : 'No bucket data available for the selected asset class and period.'}
                        </td>
                      </tr>
                    )}
                  </>
                ) : (
                  <>
                    {filteredSectorDetails.map((s, i) => {
                      const sectorColor = SECTOR_COLORS[s.sector] || '#94a3b8';
                      return (
                        <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                          <td className="py-4 px-6 font-bold text-white">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: sectorColor }} />
                              {s.sector}
                              {s.sector === 'ABF' && (
                                <span
                                  className="rounded bg-teal-500/15 px-1.5 py-0.5 text-[10px] font-bold text-teal-300"
                                  title="No ABF benchmark series exists. The sector is measured against its own return, so its whole contribution to active return shows as allocation, and selection is 0."
                                >
                                  Not benchmarked
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "font-mono font-bold",
                                s.activeWeight >= 0 ? "text-blue-400" : "text-amber-400"
                              )}>
                                {s.activeWeight >= 0 ? '+' : ''}{s.activeWeight.toFixed(2)}%
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={cn("font-mono", s.allocationEffect >= 0 ? "text-emerald-400" : "text-red-400")}>
                              {s.allocationEffect >= 0 ? '+' : ''}{s.allocationEffect.toFixed(4)}%
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span className={cn("font-mono", s.selectionEffect >= 0 ? "text-emerald-400" : "text-red-400")}>
                              {s.selectionEffect >= 0 ? '+' : ''}{s.selectionEffect.toFixed(4)}%
                            </span>
                          </td>
                          <td className="py-4 px-6 text-slate-500 text-xs italic">—</td>
                          <td className="py-4 px-6 text-right font-bold text-white">
                            {s.totalEffect >= 0 ? '+' : ''}{s.totalEffect.toFixed(4)}%
                          </td>
                        </tr>
                      );
                    })}
                    {filteredSectorDetails.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-500 italic text-sm">
                          No sector data available for the selected filter.
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>

        {/* Alternatives Performance Panel */}
        {hasAlternativesPerformance && (
          <GlassCard className="overflow-hidden">
            <div className="p-6 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-indigo-400" />
                <h3 className="text-lg font-bold text-white">Alternatives Performance</h3>
              </div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Fund-Level Returns</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
                    <th className="py-4 px-6">Fund Name</th>
                    <th className="py-4 px-4">Type</th>
                    <th className="py-4 px-4">Vintage</th>
                    <th className="py-4 px-4">IRR</th>
                    <th className="py-4 px-4">TVPI</th>
                    <th className="py-4 px-4">DPI</th>
                    <th className="py-4 px-4">MOIC</th>
                    <th className="py-4 px-4 text-right">PME</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {alternativesData!.fundPerformance!.map((fund, i) => (
                    <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                      <td className="py-4 px-6 font-bold text-white">{fund.fundName}</td>
                      <td className="py-4 px-4">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                          {fund.type}
                        </span>
                      </td>
                      <td className="py-4 px-4 font-mono text-slate-300">{fund.vintage}</td>
                      <td className={cn("py-4 px-4 font-mono font-bold", irrColor(fund.irr))}>
                        {fund.irr != null ? `${fund.irr.toFixed(1)}%` : '—'}
                      </td>
                      <td className="py-4 px-4 font-mono text-white">{fund.tvpi != null ? `${fund.tvpi.toFixed(2)}x` : '—'}</td>
                      <td className="py-4 px-4 font-mono text-white">{fund.dpi != null ? `${fund.dpi.toFixed(2)}x` : '—'}</td>
                      <td className="py-4 px-4 font-mono text-white">{fund.moic != null ? `${fund.moic.toFixed(2)}x` : '—'}</td>
                      <td className={cn(
                        "py-4 px-4 text-right font-mono font-bold",
                        fund.pme == null ? "text-slate-400" : fund.pme >= 1.0 ? "text-emerald-400" : "text-red-400"
                      )}>
                        {fund.pme != null ? `${fund.pme.toFixed(2)}x` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}
      </div>

      <LoanExplainDrawer
        loan={selectedLoan}
        risk={selectedLoan ? pcLlRiskByKey.get(`${selectedLoan.loanSource}-${selectedLoan.loanId}`) ?? null : null}
        onClose={() => setSelectedLoanKey(null)}
      />
    </Container>
  );
};

export default PerformanceAttributionScreen;
